import type {
  RelativeCurrentResponse,
  RelativeInviteResponse,
  RelativeJoinGrant,
  RelativeResolveResponse,
} from "@shared/types/callRelative";
import type { WebPushSubscription } from "@shared/types/foyer";

/**
 * The fetch client for the relative PWA (VC-22). All calls are `same-origin`
 * with credentials, so the `foyer_call` cookie rides automatically once the
 * token has been traded. Errors surface as a typed `code` the UI localizes -
 * network failures become `"network"`, never an unhandled reject on a phone a
 * family member is holding.
 */

export class CallApiError extends Error {
  constructor(
    public status: number,
    public code: string
  ) {
    super(code);
  }
}

async function parse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let code = `http_${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) code = body.error;
    } catch {
      /* ignore */
    }
    throw new CallApiError(res.status, code);
  }
  return res.json() as Promise<T>;
}

async function req<T>(url: string, method: "GET" | "POST"): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, { method, credentials: "same-origin" });
  } catch {
    throw new CallApiError(0, "network");
  }
  return parse<T>(res);
}

async function reqJson<T>(url: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new CallApiError(0, "network");
  }
  return parse<T>(res);
}

/** Trade the printed `/c/<token>` for the scoped session cookie + landing data. */
export function resolveToken(token: string): Promise<RelativeResolveResponse> {
  return req(`/api/call/resolve/${encodeURIComponent(token)}`, "POST");
}

/** Re-read the landing payload for an already-established cookie (re-open). */
export function fetchSession(): Promise<RelativeResolveResponse> {
  return req("/api/call/session", "GET");
}

/** Poll: is anything ringing/active for this phone's room right now? */
export function fetchCurrent(): Promise<RelativeCurrentResponse> {
  return req("/api/call/current", "GET");
}

/** Answer a ringing call → the join grant to enter its media room. */
export function answerCall(callId: string): Promise<RelativeJoinGrant> {
  return req(`/api/call/${encodeURIComponent(callId)}/answer`, "POST");
}

/**
 * Ring the room (VC-26). Unlike the other calls, a blocked invite is an EXPECTED
 * outcome, not an error: the server returns `{ ok: false, reason, message }`
 * with a non-2xx status carrying the honest reason. So we read the body on any
 * status other than an auth failure and hand the typed union back to the UI,
 * which renders `message` verbatim. Only a lost session (401) and a network
 * failure surface as thrown `CallApiError`s.
 */
export async function inviteRoom(): Promise<RelativeInviteResponse> {
  let res: Response;
  try {
    res = await fetch("/api/call/invite", {
      method: "POST",
      credentials: "same-origin",
    });
  } catch {
    throw new CallApiError(0, "network");
  }
  if (res.status === 401) throw new CallApiError(401, "unauthorized");
  try {
    return (await res.json()) as RelativeInviteResponse;
  } catch {
    throw new CallApiError(res.status, `http_${res.status}`);
  }
}

/** Decline a ringing call. */
export function declineCall(callId: string): Promise<{ ok: true }> {
  return req(`/api/call/${encodeURIComponent(callId)}/decline`, "POST");
}

/** Hang up / end a call. Best-effort - the SFU also reaps the room on leave. */
export function endCall(callId: string): Promise<{ ok: true }> {
  return req(`/api/call/${encodeURIComponent(callId)}/end`, "POST");
}

/* ── Web Push (VC-23) ─────────────────────────────────────────────────────── */

/** The VAPID public key the browser subscribes with. */
export function fetchPushKey(): Promise<{ publicKey: string }> {
  return req("/api/call/push/key", "GET");
}

/** Register this phone's push subscription for the session's contact. */
export function subscribePush(
  subscription: WebPushSubscription,
  label?: string
): Promise<{ ok: true; id: string }> {
  return reqJson("/api/call/push/subscribe", { subscription, label });
}

/** Turn this phone's background ring off again. */
export function unsubscribePush(endpoint: string): Promise<{ ok: true }> {
  return reqJson("/api/call/push/unsubscribe", { endpoint });
}

/**
 * Redeem a single-use join link from a push notification. Establishes the
 * session cookie and returns the landing payload plus whatever is ringing, so
 * the app can open straight onto the ring screen.
 */
export function consumeJoinLink(token: string): Promise<{
  session: RelativeResolveResponse;
  current: RelativeCurrentResponse;
}> {
  return req(`/api/call/join/${encodeURIComponent(token)}`, "POST");
}
